import Application from "@/types/application";
import {
  useSuspenseInfiniteQuery,
  useQueryClient,
  InvalidateQueryFilters,
} from "@tanstack/react-query";

type ParamProps = {
  perPage?: number;
  search?: string;
  preview?: boolean;
  sortBy?: string;
};

type ApplicationSection = {
  applications: Application[];
  count: number;
  total_pages: number;
};

type ApplicationsResponse = {
  active?: ApplicationSection;
  inactive?: ApplicationSection;
  total_applications: number;
};

const PAGE_URL = "/applications.json";

const buildURL = ({
  page,
  perPage,
  activeOnly,
  inactiveOnly,
  search,
  sortBy,
}: ParamProps & { page: number; activeOnly?: boolean; inactiveOnly?: boolean }) => {
  const searchParams: Record<string, string> = {};

  searchParams["page"] = page.toString();

  if (activeOnly) {
    searchParams["active_only"] = "true";
  }

  if (inactiveOnly) {
    searchParams["inactive_only"] = "true";
  }

  if (perPage) {
    searchParams["perPage"] = perPage.toString();
  }

  if (search) {
    searchParams["search"] = search;
  }

  if (sortBy) {
    searchParams["sort_by"] = sortBy;
  }

  const queryString = new URLSearchParams(searchParams).toString();
  return `${PAGE_URL}?${queryString}`;
};

const buildQueryKey = (type: "active" | "inactive", params: ParamProps) => {
  const key: (string | number | boolean)[] = ["applications", type];

  if (params.perPage) {
    key.push(params.perPage);
  }

  if (params.search) {
    key.push(`search:${params.search}`);
  }

  if (params.sortBy) {
    key.push(`sort_by:${params.sortBy}`);
  }

  return key;
};

const useApplicationsInfinite = (type: "active" | "inactive", params: ParamProps = {}) => {
  const queryKey = buildQueryKey(type, params);

  const fetchApplications = async ({
    pageParam,
  }: {
    pageParam: number;
  }): Promise<ApplicationsResponse> => {
    const response = await fetch(
      buildURL({
        ...params,
        page: pageParam,
        activeOnly: type === "active" || undefined,
        inactiveOnly: type === "inactive" || undefined,
      }),
    );
    return response.json();
  };

  const query = useSuspenseInfiniteQuery({
    queryKey,
    queryFn: fetchApplications,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const section = lastPage[type];
      if (section && section.total_pages > allPages.length) {
        return allPages.length + 1;
      }
      return undefined;
    },
    staleTime: 30 * 1000,
  });

  const firstPage = query.data.pages[0]?.[type];
  const applications = query.data.pages.flatMap((page) => page[type]?.applications ?? []);

  return {
    ...query,
    applications,
    count: firstPage?.count ?? 0,
    totalPages: firstPage?.total_pages ?? 1,
  };
};

const emptySection = () => ({
  applications: [] as Application[],
  count: 0,
  totalPages: 1,
  hasNextPage: false,
  isFetchingNextPage: false,
  fetchNextPage: () => Promise.resolve({} as never),
});

const useApplications = (params: ParamProps = {}) => {
  if (params.preview) {
    return {
      isLoading: false,
      active: emptySection(),
      inactive: emptySection(),
      invalidateQuery: () => {},
    };
  }
  const queryClient = useQueryClient();

  const activeQuery = useApplicationsInfinite("active", params);
  const inactiveQuery = useApplicationsInfinite("inactive", params);

  const invalidateQuery = () => {
    queryClient.invalidateQueries(
      buildQueryKey("active", params) as InvalidateQueryFilters<unknown>,
    );
    queryClient.invalidateQueries(
      buildQueryKey("inactive", params) as InvalidateQueryFilters<unknown>,
    );
  };

  return {
    isLoading: activeQuery.isLoading || inactiveQuery.isLoading,
    active: {
      applications: activeQuery.applications,
      count: activeQuery.count,
      totalPages: activeQuery.totalPages,
      hasNextPage: activeQuery.hasNextPage,
      isFetchingNextPage: activeQuery.isFetchingNextPage,
      fetchNextPage: activeQuery.fetchNextPage,
    },
    inactive: {
      applications: inactiveQuery.applications,
      count: inactiveQuery.count,
      totalPages: inactiveQuery.totalPages,
      hasNextPage: inactiveQuery.hasNextPage,
      isFetchingNextPage: inactiveQuery.isFetchingNextPage,
      fetchNextPage: inactiveQuery.fetchNextPage,
    },
    invalidateQuery,
  };
};

export default useApplications;
